import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Send, Loader, AlertCircle, MessageSquare, HelpCircle, ArrowLeft } from 'lucide-react'
import { AIMessage } from '../../types/ai'

type ModalView = 'selection' | 'chat' | 'specialized'

interface AIAssistantModalProps {
  isOpen: boolean
  onClose: () => void
  messages: AIMessage[]
  isLoading: boolean
  error: string | null
  onSendMessage: (message: string, context?: string) => void
  screenContext?: string
  onOpenSpecializedQuestions?: () => void
}

export const AIAssistantModal: React.FC<AIAssistantModalProps> = ({
  isOpen,
  onClose,
  messages,
  isLoading,
  error,
  onSendMessage,
  screenContext,
  onOpenSpecializedQuestions,
}) => {
  const [inputValue, setInputValue] = useState('')
  const [currentView, setCurrentView] = useState<ModalView>('selection')

  // Reset view when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentView('selection')
    }
  }, [isOpen])

  const handleSendMessage = () => {
    if (inputValue.trim() && !isLoading) {
      onSendMessage(inputValue, screenContext)
      setInputValue('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-40"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed bottom-4 right-4 w-full md:w-[600px] h-[600px] z-50 flex flex-col rounded-2xl border-2 shadow-2xl"
            style={{
              backgroundColor: 'var(--bg-card)',
              borderColor: 'var(--border-primary)',
              maxHeight: '90vh',
              marginRight: '24px',
              marginBottom: '24px',
            }}
          >
            {/* Header */}
            <motion.div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: 'var(--border-primary)' }}
            >
              <div className="flex items-center gap-3">
                {currentView !== 'selection' && (
                  <button
                    onClick={() => setCurrentView('selection')}
                    className="p-1 hover:bg-opacity-10 rounded-lg transition-colors mr-1"
                    style={{ color: 'var(--text-secondary)' }}
                    title="Voltar"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="flex-shrink-0"
                >
                  <img
                    src="/logo.png"
                    alt="Lognex Logo"
                    className="w-8 h-8 object-contain"
                  />
                </motion.div>
                <div>
                  <h3
                    className="font-semibold text-base"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {currentView === 'selection' ? 'Assistente IA' : currentView === 'chat' ? 'Chat IA' : 'Perguntas Especializadas'}
                  </h3>
                  <p
                    className="text-xs mt-0.5"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {currentView === 'selection' ? 'Escolha uma opção' : 'Limite: 10 perguntas por hora'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-opacity-10 rounded-lg transition-colors"
                style={{ color: 'var(--text-secondary)' }}
              >
                <X className="w-5 h-5" />
              </button>
            </motion.div>

            {/* Selection View */}
            {currentView === 'selection' && (
              <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center mb-4"
                >
                  <div className="text-6xl mb-4">🤖</div>
                  <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                    Como posso ajudar?
                  </h2>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Escolha uma das opções abaixo
                  </p>
                </motion.div>

                {/* Option 1: Specialized Questions */}
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (onOpenSpecializedQuestions) {
                      onClose()
                      onOpenSpecializedQuestions()
                    }
                  }}
                  className="w-full max-w-sm p-5 rounded-xl border-2 transition-all duration-200 hover:shadow-lg text-left"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    borderColor: 'var(--border-primary)',
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: 'var(--primary)', opacity: 0.9 }}
                    >
                      <HelpCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base mb-1" style={{ color: 'var(--text-primary)' }}>
                        Perguntas Especializadas
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Acesse perguntas pré-definidas com análises detalhadas sobre seus dados
                      </p>
                    </div>
                  </div>
                </motion.button>

                {/* Option 2: Free Chat */}
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setCurrentView('chat')}
                  className="w-full max-w-sm p-5 rounded-xl border-2 transition-all duration-200 hover:shadow-lg text-left"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    borderColor: 'var(--border-primary)',
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="p-3 rounded-xl"
                      style={{ backgroundColor: '#10b981' }}
                    >
                      <MessageSquare className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-base mb-1" style={{ color: 'var(--text-primary)' }}>
                        Chat Livre
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Faça perguntas livres sobre os dados da tela atual
                      </p>
                    </div>
                  </div>
                </motion.button>
              </div>
            )}

            {/* Chat View */}
            {currentView === 'chat' && (
              <>
                {/* Messages Container */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 && !error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex flex-col items-center justify-center h-full gap-4"
                    >
                      <div className="text-6xl">💬</div>
                      <p className="text-base font-semibold text-center" style={{ color: 'var(--text-primary)' }}>
                        Chat Livre
                      </p>
                      <p className="text-sm text-center max-w-xs" style={{ color: 'var(--text-secondary)' }}>
                        Faça uma pergunta sobre os dados da tela e receba análises inteligentes
                      </p>
                    </motion.div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex gap-2 p-3 rounded-lg border-2"
                      style={{
                        backgroundColor: '#ef444420',
                        borderColor: '#ef4444',
                      }}
                    >
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-600">Erro</p>
                        <p className="text-xs text-red-500">{error}</p>
                      </div>
                    </motion.div>
                  )}

                  {/* Messages */}
                  {messages.map((message, index) => (
                    <motion.div
                      key={message.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`flex ${
                        message.type === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                          message.type === 'user'
                            ? 'rounded-br-none'
                            : 'rounded-bl-none'
                        }`}
                        style={{
                          backgroundColor:
                            message.type === 'user'
                              ? 'var(--primary)'
                              : 'var(--bg-tertiary)',
                          color:
                            message.type === 'user'
                              ? 'white'
                              : 'var(--text-primary)',
                        }}
                      >
                        <p className="break-words">{message.content}</p>
                        {message.cached && (
                          <p
                            className="text-xs mt-1 opacity-70"
                            style={{
                              color:
                                message.type === 'user'
                                  ? 'rgba(255,255,255,0.7)'
                                  : 'var(--text-secondary)',
                            }}
                          >
                            (em cache)
                          </p>
                        )}
                      </div>
                    </motion.div>
                  ))}

                  {/* Loading Indicator */}
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex gap-2 p-3 rounded-lg"
                      style={{ backgroundColor: 'var(--bg-tertiary)' }}
                    >
                      <Loader className="w-4 h-4 animate-spin" style={{ color: 'var(--primary)' }} />
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Processando...
                      </p>
                    </motion.div>
                  )}
                </div>

                {/* Input Area */}
                <motion.div
                  className="flex gap-3 p-5 border-t"
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  <textarea
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Digite sua pergunta..."
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 rounded-lg border resize-none focus:outline-none focus:ring-2 text-base"
                    style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      borderColor: 'var(--border-primary)',
                      color: 'var(--text-primary)',
                      '--tw-ring-color': 'var(--primary)',
                    } as React.CSSProperties}
                    rows={3}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    className="px-5 py-3 rounded-lg font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50 flex-shrink-0 flex items-center justify-center"
                    style={{
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                    }}
                    title={isLoading ? 'Processando...' : 'Enviar (Enter)'}
                  >
                    {isLoading ? (
                      <Loader className="w-6 h-6 animate-spin" />
                    ) : (
                      <Send className="w-6 h-6" />
                    )}
                  </button>
                </motion.div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
